import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * PdfLightbox
 * Props:
 * - open: boolean
 * - src: string (public-relative path e.g. '/Explore Product/1.pdf')
 * - title: string
 * - onClose: function
 */
export default function PdfLightbox({ open, src, title = 'Preview', onClose }) {
  const wrapperRef = useRef(null);
  const frameRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  const requestFs = async () => {
    try {
      const el = frameRef.current || wrapperRef.current;
      if (!el) return;
      if (!document.fullscreenElement) {
        if (el.requestFullscreen) await el.requestFullscreen();
        // @ts-ignore vendor prefixes
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        // @ts-ignore
        else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
        // @ts-ignore
        else if (el.msRequestFullscreen) el.msRequestFullscreen();
        setIsFs(true);
      }
    } catch { }
  };

  const exitFs = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setIsFs(false);
    } catch { }
  };

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') {
      if (isFs) exitFs();
      if (typeof onClose === 'function') onClose();
    }
  }, [isFs, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    const onFsChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Best-effort prevent saving: hide built-in toolbar and prevent context menu
  // Note: client-side cannot fully prevent downloads of public content.
  const encoded = encodeURI(src);
  const viewerParams = isFs
    ? '#toolbar=0&navpanes=0&scrollbar=0&zoom=page-width'
    : '#toolbar=0&navpanes=0&scrollbar=0&zoom=page-fit';

  if (!open) return null;

  return (
    <>
      <style jsx>{`
        @keyframes lbFade {
          from { opacity: 0 }
          to { opacity: 1 }
        }

        .pdf-lightbox {
          animation: lbFade 0.18s ease-out;
        }

        .pdf-frame:fullscreen,
        .pdf-frame:-webkit-full-screen,
        .pdf-frame:-ms-fullscreen,
        .pdf-stage:fullscreen .pdf-frame,
        .pdf-stage:-webkit-full-screen .pdf-frame,
        .pdf-stage:-ms-fullscreen .pdf-frame,
        .pdf-lightbox:fullscreen .pdf-frame,
        .pdf-lightbox:-webkit-full-screen .pdf-frame,
        .pdf-lightbox:-ms-fullscreen .pdf-frame {
          width: 100vw !important;
          height: 100vh !important;
          max-width: 100vw !important;
          max-height: 100vh !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          background: #000 !important;
        }

        .pdf-lightbox.is-fs .pdf-stage {
          padding: 0;
        }
        .pdf-lightbox.is-fs .pdf-frame {
          width: 100vw;
          height: 100vh;
          border-radius: 0;
          box-shadow: none;
          background: #000;
        }
        .pdf-lightbox.is-fs .pdf-chrome {
          position: fixed;
          inset: 12px 12px auto 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          z-index: 10000;
          background: linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0));
          border-radius: 10px;
          padding: 8px 10px;
          pointer-events: none;
        }
        .pdf-lightbox.is-fs .pdf-title {
          color: #ffffff;
          text-shadow: 0 2px 8px rgba(0,0,0,0.6);
          font-weight: 800;
        }
        .pdf-lightbox.is-fs .pdf-actions {
          pointer-events: auto;
        }
        .pdf-lightbox.is-fs .pdf-btn {
          background: rgba(0,0,0,0.55);
          border-color: rgba(255,255,255,0.24);
        }
        .pdf-lightbox.is-fs .pdf-btn:hover {
          background: rgba(0,0,0,0.75);
        }
      `}</style>

      <div
        ref={wrapperRef}
        className={`pdf-lightbox fixed inset-0 z-[9999] grid grid-rows-[auto_1fr] backdrop-blur-md ${isFs ? 'is-fs' : ''}`}
        style={{
          background: 'radial-gradient(120% 120% at 50% 0%, rgba(0,0,0,0.62), rgba(0,0,0,0.78))'
        }}
        role="dialog"
        aria-modal="true"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="pdf-chrome flex items-center justify-between gap-2.5 px-3.5 py-2.5 text-[#e9f2ee]">
          <div className="pdf-title font-bold tracking-wide" title={title}>{title}</div>
          <div className="pdf-actions flex items-center gap-2">
            {!isFs ? (
              <button className="pdf-btn border border-white/20 bg-white/10 text-white rounded-lg px-2.5 py-2 cursor-pointer transition-all hover:bg-white/15 hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-px" onClick={requestFs} title="Enter fullscreen">⤢</button>
            ) : (
              <button className="pdf-btn border border-white/20 bg-white/10 text-white rounded-lg px-2.5 py-2 cursor-pointer transition-all hover:bg-white/15 hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-px" onClick={exitFs} title="Exit fullscreen">⤡</button>
            )}
            <button className="pdf-btn border border-white/20 bg-white/10 text-white rounded-lg px-2.5 py-2 cursor-pointer transition-all hover:bg-white/15 hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-px" onClick={() => { if (isFs) exitFs(); if (typeof onClose === 'function') onClose(); }} title="Close">✕</button>
          </div>
        </div>
        <div className="pdf-stage grid place-items-center p-[clamp(8px,2vw,16px)] overflow-hidden" onClick={(e) => { if (e.target === e.currentTarget) { if (isFs) exitFs(); if (typeof onClose === 'function') onClose(); } }}>
          <iframe
            ref={frameRef}
            className="pdf-frame w-[min(980px,88vw)] h-[min(45vh,calc(88vw*1.25))] border-0 rounded-2xl bg-[#fcfcfc0d]"
            style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.08) inset'
            }}
            title={title}
            src={`${encoded}${viewerParams}`}
            key={`pdf-iframe-${isFs ? 'fs' : 'norm'}`}
            allow="fullscreen"
            allowFullScreen
            loading="eager"
          />
        </div>
      </div>
    </>
  );
}
